import { Helmet } from "react-helmet-async";
import { Type } from "@themes/pollinator/ui/Type";
import { MdxRenderer } from "@themes/pollinator/ui/MdxRenderer";
import { MarketingSection, marketingAccent, marketingType } from "@platform/features/marketing";
import { newsType } from "../../news.type";
import { newsPostComponents } from "./newsPostComponents";
import type { NewsPost as NewsPostData } from "../../news.types";

export type Props = {
  post: NewsPostData;
};

function formatDate(value: string | null) {
  if (!value) return null;
  return new Date(value).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * One post: its kind, its title and date across the page, then the writing
 * in a 720px column, drawn as the design draws it.
 *
 * The writing is the article as staff wrote it in Django admin, in Markdown,
 * which may use the design's picture with a credit (`<Figure>`) and its
 * call-out (`<Callout>`). The band ends in a rule, and what follows it (the
 * News section and Get Updates) is the page's to add.
 */
export function NewsPost({ post }: Props) {
  const published = formatDate(post.published_at);
  const title = ["Pollinator", post.title].filter(Boolean).join(" | ");

  return (
    <>
      {/* The layout around the post already says its address; these name the post itself. */}
      <Helmet>
        <title>{title}</title>
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta name="twitter:title" content={title} />
        {post.excerpt && <meta name="description" content={post.excerpt} />}
        {post.excerpt && <meta property="og:description" content={post.excerpt} />}
        {post.excerpt && <meta name="twitter:description" content={post.excerpt} />}
      </Helmet>

      <MarketingSection id="post" className="border-b border-brand-700">
        <article className="flex w-full flex-col gap-6 lg:gap-8" data-testid="news-post">
          <header className="flex flex-col gap-4">
            {post.label && (
              <Type
                variant="caption"
                as="p"
                className={`${marketingType.eyebrow} ${marketingAccent} self-start max-w-none`}
              >
                {post.label}
              </Type>
            )}
            <Type variant="h3" as="h1" className={`${marketingType.display} font-bold md:font-bold`}>
              {post.title}
            </Type>
            {published && (
              <Type variant="body2" as="p" className={newsType.date}>
                <time dateTime={post.published_at ?? undefined}>{published}</time>
              </Type>
            )}
          </header>
          <MdxRenderer
            content={post.content || ""}
            components={newsPostComponents}
            className="w-full max-w-[720px]"
          />
        </article>
      </MarketingSection>
    </>
  );
}

export default NewsPost;
